import { createStore, useStore } from '@w/react-store'
import { RUNTIME_PROBE, isRuntimeReply, runtimeError } from './protocol'
import type { RuntimeEndpoint, RuntimeOperation, RuntimeWorker, OperationKind, WorkerSnapshot } from './protocol'

export type { RuntimeOperation, RuntimeWorker, OperationKind } from './protocol'
export interface RuntimeStorageEntry { name: string; kind: string; bytes?: number }
export interface RuntimeStorage {
  status: 'idle' | 'loading' | 'ready' | 'partial'
  entries: RuntimeStorageEntry[]
  notes: string[]
  usage?: number
  quota?: number
  persisted?: boolean
  checkedAt?: number
}
export interface RuntimeSnapshot {
  now: number
  overview: Record<string, string>
  operations: RuntimeOperation[]
  workers: RuntimeWorker[]
  storage: RuntimeStorage
  droppedOperations: number
}
export interface OperationInput { name: string; kind: OperationKind; parentId?: string; resourceId?: string; detail?: string; status?: 'running' | 'queued' | 'waiting' }
export interface RuntimeTask {
  id: string
  update(update: Partial<Pick<RuntimeOperation, 'status' | 'waitingFor' | 'detail'>>): void
  finish(error?: unknown): void
  run<T>(input: Omit<OperationInput, 'parentId'>, fn: (task: RuntimeTask) => Promise<T>): Promise<T>
}

/** One owner per JS context; live state is independent of telemetry and application databases. */
export class RuntimeMonitor {
  private store = createStore<RuntimeSnapshot>({ now: Date.now(), overview: {}, operations: [], workers: [], storage: { status: 'idle', entries: [], notes: [] }, droppedOperations: 0 })
  private sequence = 0
  private probes = new Map<string, () => void>()

  useSnapshot = (): RuntimeSnapshot => useStore(this.store)
  snapshot(): RuntimeSnapshot { return this.store.getState() }
  setOverview(values: Record<string, string>): void {
    this.store.update(state => ({ ...state, overview: { ...state.overview, ...values } }))
  }
  setStorage(storage: RuntimeStorage): void { this.store.update(state => ({ ...state, storage })) }
  tick(): void { this.store.update(state => ({ ...state, now: Date.now() })) }

  begin(input: OperationInput): RuntimeTask {
    const id = `operation-${++this.sequence}`
    const operation: RuntimeOperation = { ...input, id, startedAt: Date.now(), status: input.status ?? 'running' }
    this.store.update(state => {
      const kept = boundOperations(state.operations, 499)
      return { ...state, operations: [...kept, operation], droppedOperations: state.droppedOperations + state.operations.length - kept.length }
    })
    let finished = false
    return {
      id,
      update: update => { if (!finished) this.changeOperation(id, update) },
      finish: error => {
        if (finished) return
        finished = true
        this.changeOperation(id, { status: error === undefined ? 'completed' : 'failed', endedAt: Date.now(), error: error === undefined ? undefined : runtimeError(error), waitingFor: undefined })
      },
      run: (child, fn) => this.run({ ...child, parentId: id }, fn),
    }
  }

  async run<T>(input: OperationInput, fn: (task: RuntimeTask) => Promise<T>): Promise<T> {
    const task = this.begin(input)
    try {
      const value = await fn(task)
      task.finish()
      return value
    } catch (error) {
      task.finish(error === undefined ? new Error('Operation failed') : error)
      throw error
    }
  }

  private changeOperation(id: string, update: Partial<RuntimeOperation>): void {
    this.store.update(state => ({ ...state, operations: state.operations.map(value => value.id === id ? { ...value, ...update } : value) }))
  }

  observeWorker(endpoint: RuntimeEndpoint, input: Pick<RuntimeWorker, 'name' | 'kind' | 'parentId'>): { id: string; close(): void; fail(error: unknown): void; probe(): void } {
    const id = `worker-${++this.sequence}`
    let closed = false
    let pendingId: string | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    this.store.update(state => ({ ...state, workers: [...state.workers.slice(-127), { ...input, id, status: 'unknown', startedAt: Date.now() }] }))
    const update = (values: Partial<RuntimeWorker>) => this.store.update(state => ({ ...state, workers: state.workers.map(worker => worker.id === id ? { ...worker, ...values } : worker) }))
    const listener = (event: MessageEvent<unknown>) => {
      if (closed || !isRuntimeReply(event.data) || event.data.id !== pendingId) return
      clearTimeout(timer)
      pendingId = undefined
      update({ status: 'responsive', lastResponseAt: Date.now(), error: undefined })
      this.acceptWorkerSnapshot(id, event.data.snapshot)
    }
    const fail = (error: unknown) => {
      if (closed) return
      clearTimeout(timer)
      pendingId = undefined
      update({ status: 'failed', error: runtimeError(error) })
    }
    const probe = () => {
      if (closed || pendingId) return
      pendingId = `${id}:${++this.sequence}`
      update({ status: 'checking', checkedAt: Date.now() })
      timer = setTimeout(() => { pendingId = undefined; update({ status: 'unresponsive' }) }, 3_000)
      try { endpoint.postMessage({ type: RUNTIME_PROBE, id: pendingId }) }
      catch (error) { clearTimeout(timer); pendingId = undefined; fail(error) }
    }
    endpoint.addEventListener('message', listener)
    this.probes.set(id, probe)
    probe()
    return { id, probe, fail, close: () => {
      if (closed) return
      closed = true
      clearTimeout(timer)
      this.probes.delete(id)
      endpoint.removeEventListener('message', listener)
      update({ status: 'closed' })
      this.store.update(state => ({ ...state,
        workers: state.workers.map(value => value.id.startsWith(`${id}:`) ? { ...value, status: 'closed' } : value),
        operations: state.operations.map(value => (value.resourceId === id || value.resourceId?.startsWith(`${id}:`)) && value.endedAt === undefined ? { ...value, status: 'failed', error: 'Worker closed', endedAt: Date.now() } : value),
      }))
    } }
  }

  private acceptWorkerSnapshot(id: string, snapshot: WorkerSnapshot): void {
    const prefix = `${id}:`
    const operations = snapshot.operations.slice(-200).map(value => ({ ...value, id: `${prefix}${value.id}`, resourceId: value.resourceId ? `${prefix}${value.resourceId}` : id, parentId: value.parentId ? `${prefix}${value.parentId}` : undefined }))
    const workers = snapshot.workers.slice(-50).map(value => ({ ...value, id: `${prefix}${value.id}`, parentId: value.parentId ? `${prefix}${value.parentId}` : id }))
    this.store.update(state => {
      const incoming = [...state.operations.filter(value => !value.id.startsWith(prefix)), ...operations]
      const kept = boundOperations(incoming, 500)
      return { ...state, operations: kept, droppedOperations: state.droppedOperations + incoming.length - kept.length, workers: [...state.workers.filter(value => !value.id.startsWith(prefix)), ...workers].slice(-128) }
    })
  }

  probeWorkers(): void { for (const probe of this.probes.values()) probe() }
  report(): string { return JSON.stringify({ format: 'runtime-report-v1', ...this.snapshot(), exportedAt: new Date().toISOString() }, null, 2) }
}

/** Retain unfinished work preferentially, including an early startup that never settled. */
function boundOperations(operations: RuntimeOperation[], limit: number): RuntimeOperation[] {
  if (operations.length <= limit) return operations
  const active = operations.filter(value => value.endedAt === undefined)
  const ended = operations.filter(value => value.endedAt !== undefined)
  const endedCount = Math.max(0, limit - active.length)
  const keep = new Set([...active.slice(-limit), ...(endedCount ? ended.slice(-endedCount) : [])].map(value => value.id))
  return operations.filter(value => keep.has(value.id))
}

export const runtimeMonitor = new RuntimeMonitor()
export function useRuntimeSnapshot(monitor = runtimeMonitor): RuntimeSnapshot { return monitor.useSnapshot() }
